import { useState } from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContent } from "../../../components/PageContent";
import { PageBreadcrumb } from "../../../components/PageBreadcrumb";
import { Card } from "../../../components/Card";
import { Button } from "../../../components/Button";
import { Input } from "../../../components/Input";
import { FormLabel } from "../../../components/FormLabel";
import { InfoBox } from "../../../components/InfoBox";
import { LoadingContainer } from "../../../components/LoadingContainer";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { SidebarPanel } from "../../../components/SidebarPanel";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../../../components/Table";
import { useGetPaymentMethodQuery } from "../../../query/paymentMethodsQuery";
import {
  VIVA_TRANSACTIONS_MAX_DAYS,
  useGetInfiniteVivaTransactionsQuery,
} from "../../../query/vivaQuery";
import { useInfiniteScroll } from "../../../hooks/useInfiniteScroll";
import { ApiError } from "../../../utils/api";
import { formatDateTime, toDateString } from "../../../utils/dateUtils";
import type { PaymentMethodId, StoreId } from "@KassaroAB/shared/types/ids";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { spacing } from "@styling/sizing";

type VivaTransaction = Record<string, unknown>;

const JsonBlock = styled.pre`
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: ${fontSizes.small}px;
  color: ${colors.text};
  white-space: pre-wrap;
  word-break: break-word;
`;

const MonoText = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: ${fontSizes.small}px;
`;

const FilterForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spacing.x2}px;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 180px;
`;

const Summary = styled.div`
  margin-top: ${spacing.x2}px;
  color: ${colors.textMuted};
`;

const ScrollFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.x1}px;
  padding: ${spacing.x3}px;
  color: ${colors.textMuted};
`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readPath = (record: VivaTransaction, path: string[]): unknown =>
  path.reduce<unknown>(
    (current, key) => (isRecord(current) ? current[key] : undefined),
    record,
  );

const readField = (record: VivaTransaction, ...paths: string[][]): string => {
  for (const path of paths) {
    const value = readPath(record, path);
    if (typeof value === "string" || typeof value === "number") {
      return String(value);
    }
  }
  return "-";
};

const formatInsDate = (transaction: VivaTransaction): string => {
  const raw = readField(transaction, ["InsDate"]);
  const formatted = formatDateTime(raw);
  return formatted === "-" ? raw : formatted;
};

const errorDetails = (error: unknown): unknown =>
  error instanceof ApiError ? error.data : undefined;

export const VivaTransactionsView = () => {
  const { t } = useTranslation();
  const { storeId, paymentMethodId } = useParams<{
    storeId: StoreId;
    paymentMethodId: PaymentMethodId;
  }>();
  const [fromDate, setFromDate] = useState(() => toDateString(new Date()));
  const [filterDate, setFilterDate] = useState(fromDate);
  const [selected, setSelected] = useState<VivaTransaction | null>(null);

  const { data: paymentMethod } = useGetPaymentMethodQuery(
    storeId,
    paymentMethodId,
  );
  const {
    data,
    error,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useGetInfiniteVivaTransactionsQuery(storeId, paymentMethodId, filterDate);

  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetching,
    disabled: !!error,
    fetchNextPage,
  });

  const pages = data?.pages ?? [];
  const transactions = pages.flatMap((page) => page.transactions);
  const oldestPage = pages.at(-1);
  const merchantId = pages[0]?.merchantId;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilterDate(fromDate);
  };

  const details = errorDetails(error);
  const paymentMethodLabel = paymentMethod?.name ?? paymentMethodId ?? "";
  const listPath = `/stores/${storeId}/paymentMethods`;

  return (
    <PageContent
      title={t("vivaTransactionsView.title", { name: paymentMethodLabel })}
      actions={
        <Button
          $variant="outlined"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {t("vivaTransactionsView.refresh")}
        </Button>
      }
    >
      <PageBreadcrumb
        items={[
          { label: t("paymentMethodsView.title"), to: listPath },
          { label: paymentMethodLabel, to: `${listPath}/${paymentMethodId}` },
          { label: t("vivaTransactionsView.breadcrumb") },
        ]}
      />

      <Card>
        <FilterForm onSubmit={handleSubmit}>
          <FilterField>
            <FormLabel htmlFor="viva-transactions-date">
              {t("vivaTransactionsView.fromDate")}
            </FormLabel>
            <Input
              id="viva-transactions-date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              required
            />
          </FilterField>
          <Button type="submit" disabled={!fromDate}>
            {t("vivaTransactionsView.search")}
          </Button>
        </FilterForm>
        {merchantId && oldestPage && (
          <Summary>
            {t("vivaTransactionsView.summary", {
              count: transactions.length,
              merchantId,
              fromDate: filterDate,
              toDate: oldestPage.date,
            })}
          </Summary>
        )}
      </Card>

      {error && !isFetchNextPageError && (
        <InfoBox variant="error">
          {t("vivaTransactionsView.errorLoad", { message: error.message })}
          {details !== undefined && (
            <JsonBlock>{JSON.stringify(details, null, 2)}</JsonBlock>
          )}
        </InfoBox>
      )}

      {isLoading ? (
        <LoadingContainer message={t("vivaTransactionsView.loading")} />
      ) : (
        data && (
          <Card>
            {transactions.length > 0 && (
              <Table minWidth="1100px">
                <TableHeader>
                  <tr>
                    <TableHeaderCell>{t("vivaTransactionsView.tableTime")}</TableHeaderCell>
                    <TableHeaderCell>{t("vivaTransactionsView.tableTransactionId")}</TableHeaderCell>
                    <TableHeaderCell>{t("vivaTransactionsView.tableOrderCode")}</TableHeaderCell>
                    <TableHeaderCell align="right">{t("vivaTransactionsView.tableAmount")}</TableHeaderCell>
                    <TableHeaderCell>{t("vivaTransactionsView.tableStatus")}</TableHeaderCell>
                    <TableHeaderCell>{t("vivaTransactionsView.tableType")}</TableHeaderCell>
                    <TableHeaderCell>{t("vivaTransactionsView.tableMerchantReference")}</TableHeaderCell>
                    <TableHeaderCell>{t("vivaTransactionsView.tableSourceCode")}</TableHeaderCell>
                    <TableHeaderCell>{t("vivaTransactionsView.tableCard")}</TableHeaderCell>
                  </tr>
                </TableHeader>
                <TableBody>
                  {transactions.map((transaction, index) => (
                    <TableRow
                      key={`${readField(transaction, ["TransactionId"])}-${index}`}
                      clickable
                      onClick={() => setSelected(transaction)}
                    >
                      <TableCell noWrap>{formatInsDate(transaction)}</TableCell>
                      <TableCell noWrap>
                        <MonoText>{readField(transaction, ["TransactionId"])}</MonoText>
                      </TableCell>
                      <TableCell noWrap>
                        <MonoText>
                          {readField(transaction, ["OrderCode"], ["Order", "OrderCode"])}
                        </MonoText>
                      </TableCell>
                      <TableCell noWrap align="right">
                        {readField(transaction, ["Amount"])}
                      </TableCell>
                      <TableCell noWrap>{readField(transaction, ["StatusId"])}</TableCell>
                      <TableCell noWrap>{readField(transaction, ["TransactionTypeId"])}</TableCell>
                      <TableCell>{readField(transaction, ["MerchantTrns"])}</TableCell>
                      <TableCell noWrap>
                        {readField(transaction, ["SourceCode"], ["Source", "SourceCode"])}
                      </TableCell>
                      <TableCell noWrap>{readField(transaction, ["CardNumber"])}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <ScrollFooter ref={sentinelRef}>
              {isFetchNextPageError ? (
                <>
                  <InfoBox variant="error">
                    {t("vivaTransactionsView.errorLoad", { message: error?.message ?? "" })}
                    {details !== undefined && (
                      <JsonBlock>{JSON.stringify(details, null, 2)}</JsonBlock>
                    )}
                  </InfoBox>
                  <Button $small onClick={() => fetchNextPage()}>
                    {t("common.retry")}
                  </Button>
                </>
              ) : isFetchingNextPage ? (
                <>
                  <LoadingSpinner />
                  {t("vivaTransactionsView.loadingMore")}
                </>
              ) : hasNextPage ? (
                t("vivaTransactionsView.scrollForMore")
              ) : (
                <>
                  {transactions.length === 0 && t("vivaTransactionsView.empty")}
                  <span>
                    {t("vivaTransactionsView.limitReached", {
                      days: VIVA_TRANSACTIONS_MAX_DAYS,
                    })}
                  </span>
                </>
              )}
            </ScrollFooter>
          </Card>
        )
      )}

      {selected && (
        <SidebarPanel
          isOpen={!!selected}
          onClose={() => setSelected(null)}
          title={t("vivaTransactionsView.panelTitle", {
            id: readField(selected, ["TransactionId"]),
          })}
          wide
        >
          <JsonBlock>{JSON.stringify(selected, null, 2)}</JsonBlock>
        </SidebarPanel>
      )}
    </PageContent>
  );
};
